import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { LoadingOverlay } from '@mantine/core';
import { fetchEventById, patchEvent } from '../restapi/eventService';
import { LogS } from '../LogS';
import { EventDateS } from '../EventDateS';
import { DateUtils } from '../../Utils/dateUtils';
import { ErrorBox } from '../ResourceAdapter';
import { EventS } from '../EventS';
import { openDeleteConfirmModal } from '../../Atoms/DeleteConfirmModal';
import { MixpanelS } from '../MixpanelS';
import { ConstantS } from '../ConstantS';

export const EventContext = React.createContext(undefined);

export const EventContextProvider = () => {
  const navigate = useNavigate();
  const [event, setEvent] = useState();
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [dates, setDates] = useState([]);
  const [loadingNotifications, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const { id: eventId } = useParams();
  const [errorHttpStatus, setErrorHttpStatus] = useState();
  const [taskReloadTrigger, setTrigger] = useState(new Date());
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (event && dates.length === 0) {
      setDates(event.dates);
    }
  }, [event]);

  useEffect(() => {
    if (eventId) {
      setLoadingEvent(true);
      fetchEventById(eventId)
        .then((resp) => {
          if (resp.ok) {
            resp.json().then(setEvent);
          } else {
            const { status } = resp;
            setErrorHttpStatus(status);
          }
        })
        .finally(() => setLoadingEvent(false));
    }
  }, [eventId]);

  useEffect(() => {
    setEvent((prev) => ({ ...prev, dates }));
  }, [dates]);

  const datesHandlers = {
    append: (eventDate) => setDates(DateUtils.sortEventDates([...dates, eventDate])),
    edit: (eventDate) => setDates(DateUtils.sortEventDates(dates.map((d) => (d.id === eventDate.id ? eventDate : d)))),
    remove: (id) => setDates(dates.filter((d) => d.id !== id)),
  };
  const [portalReloadTrigger, setPortalTrigger] = useState(new Date());

  const loadNotifications = () => {
    if (eventId) {
      setLoading(true);
      LogS.fetchLogbookNotifications(eventId)
        .then(setNotifications)
        .finally(() => setLoading(false));
    }
  };

  const assignPrimaryLocation = (locationId) => {
    EventDateS.assignPrimaryLocation(locationId, eventId).then((success) => {
      if (success) {
        setDates(dates.map((d) => ({ ...d, locationId })));
      }
    });
  };

  const assignPrimaryDate = (eventDateId) =>
    setDates(dates.map((ed) => ({ ...ed, useAsPrimary: ed.id === eventDateId })));

  const detachLocation = (locationId) =>
    setDates(dates.map((d) => (d.locationId === locationId ? { ...d, locationId: null } : d)));

  const removeNotification = (notification) => setNotifications(notifications.filter((n) => n.id !== notification.id));

  // Procedures executed recently by an automation
  const dispatchProcedures = (procedures) => {
    loadNotifications();
    if (procedures.includes('EVENT_STATUS_CHANGED')) {
      refreshEvent();
    }
    if (
      procedures.includes('CREATE_TASK') ||
      procedures.includes('DELETE_TASK') ||
      procedures.includes('DELETE_TASKS') ||
      procedures.includes('COMPLETE_TASK')
    ) {
      setTrigger(new Date());
    }
    if (procedures.includes('SHARE_ENTITY')) {
      setPortalTrigger(new Date());
    }
  };

  const refreshEvent = () => {
    fetchEventById(event.id)
      .then((resp) => {
        if (resp.ok) {
          resp.json().then((event) => {
            setEvent(event);
          });
        } else {
          console.info(`could not load event ${event.id}`);
        }
      })
      .catch(() => {
        console.info(`could not load event ${event.id}`);
      });
  };

  const copyHandler = async () => {
    const onConfirm = async () => {
      if (eventId) {
        setCopying(true);
        MixpanelS.track(ConstantS.TrackingEvents.EventCopied);
        const resJSON = await EventS.copyEvent(eventId);
        setCopying(false);
        if (resJSON) {
          const { id } = resJSON;
          navigate(`/events/${id}/details`);
          return true;
        }
      }
      return false;
    };
    openDeleteConfirmModal({
      title: 'Event duplizieren?',
      description: 'Möchtest du das Event wirklich duplizieren?',
      btnColor: 'blue',
      btnText: 'actions.duplicate',
      onConfirm,
    });
  };

  useEffect(() => {
    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  return (
    <EventContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        loadingEvent,
        assignPrimaryDate,
        assignPrimaryLocation,
        detachLocation,
        portalReloadTrigger,
        datesHandlers,
        dates,
        taskReloadTrigger,
        dispatchProcedures,
        loadingNotifications,
        notifications,
        removeNotification,
        dispatchProcedures,
        copyHandler,
        event,
        handleChange: (patch) => {
          setEvent((prevState) => ({
            ...prevState,
            ...patch,
          }));
        },
        saveEvent: async (patch) => {
          const res = await patchEvent(event.id, patch);
          if (res.ok) {
            res.json().then(setEvent);
            return true;
          }
          return false;
        },
        refreshEvent,
      }}
    >
      <LoadingOverlay visible={copying} loaderProps={{ size: 'xl' }} />
      {errorHttpStatus && <ErrorBox code={errorHttpStatus} />}
      {!errorHttpStatus && <Outlet />}
    </EventContext.Provider>
  );
};
